import { INTERRUPT_OTHER_PREFIX } from '../constants';

const RECOMMENDED_SUFFIX_RE = /\s*\(recommended\)\s*$/i;

export interface IParsedInterruptOptionLabel {
  label: string;
  isRecommended: boolean;
}

/** `V1InterruptOption` has no structured "recommended" field — the agent
 *  bakes a trailing "(Recommended)" marker directly into the option's
 *  label text instead. Strips it out so it can be shown as a real Badge
 *  rather than plain text glued onto the label. */
export function parseInterruptOptionLabel(
  label: string,
): IParsedInterruptOptionLabel {
  if (!RECOMMENDED_SUFFIX_RE.test(label)) {
    return { label, isRecommended: false };
  }
  return {
    label: label.replace(RECOMMENDED_SUFFIX_RE, ''),
    isRecommended: true,
  };
}

export interface IInterruptAnswerSummary {
  /** Display text — selected option labels (marker stripped) and/or the
   *  typed custom answer, comma-joined in submission order. Empty when skipped. */
  text: string;
  /** True if any part of the answer was manually typed (the "Other" field),
   *  rather than picked from the option list. */
  isCustom: boolean;
  /** True if one of the selected options was the recommended one — the
   *  marker rides along on the submitted value itself (see
   *  `buildAnswerPayload` in RainInterruptCard), so no need to
   *  cross-reference the question's own option list. */
  hasRecommended: boolean;
  /** True when nothing was selected (the question was skipped). */
  skipped: boolean;
}

/** Summarizes one question's already-submitted answer for the collapsed,
 *  read-only "Q above / A below" view (no more per-option rows once
 *  answered — see the Figma "Chat - Submitted Single Question" reference). */
export function summarizeInterruptAnswer(
  selected: string[],
): IInterruptAnswerSummary {
  if (selected.length === 0) {
    return { text: '', isCustom: false, hasRecommended: false, skipped: true };
  }
  let isCustom = false;
  let hasRecommended = false;
  const parts = selected.map(value => {
    if (value.startsWith(INTERRUPT_OTHER_PREFIX)) {
      isCustom = true;
      return value.slice(INTERRUPT_OTHER_PREFIX.length);
    }
    const { label, isRecommended } = parseInterruptOptionLabel(value);
    if (isRecommended) hasRecommended = true;
    return label;
  });
  return { text: parts.join(', '), isCustom, hasRecommended, skipped: false };
}
